/**
 * Client entry for /character/preview and /character/create.
 *
 * Mounts the RSPK-vendored 3D preview into #character-create-preview and wires up
 * the arrow / color buttons to cycle slot & palette.
 */
import {
  BASE_APPEARANCE_SLOT_ORDER,
  BODY_COLOR_CHANNELS,
  cloneBaseCharacterAppearance,
  createDefaultBaseCharacterAppearance,
  getBaseAppearanceOptions,
  getBodyColorSwatches,
  normalizeBaseCharacterAppearance,
  type BaseAppearanceSlot,
  type BaseCharacterAppearance,
  type BodyColorChannel,
} from './baseCharacter';
import {
  CharacterCreationPreviewController,
  type PreviewEquipment,
} from './creationPreview';
import type {
  CharacterAnimationStrategy,
  EquippedAppearanceItem,
  EquipmentPresentationOverride,
} from './characterAppearance';

function readAnimationStrategy(value: string | undefined): CharacterAnimationStrategy | undefined {
  switch (value) {
    case 'current':
    case 'body-only':
    case 'armor-deform':
    case 'armor-deform-all-pivots':
    case 'full-if-rigged':
    case 'static-pose':
      return value;
    default:
      return undefined;
  }
}

function readPresentationOverrides(value: string | undefined): Record<string, EquipmentPresentationOverride> | undefined {
  if (!value) {
    return undefined;
  }

  try {
    const raw = JSON.parse(value) as Record<string, EquipmentPresentationOverride>;
    if (!raw || typeof raw !== 'object') {
      return undefined;
    }

    return Object.fromEntries(
      Object.entries(raw).map(([name, override]) => [name.toLowerCase(), override]),
    );
  } catch {
    return undefined;
  }
}

function readBooleanData(value: string | undefined): boolean | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === '0' || value === 'false' || value === 'off') {
    return false;
  }
  if (value === '1' || value === 'true' || value === 'on') {
    return true;
  }
  return undefined;
}

function readNumberData(value: string | undefined): number | undefined {
  if (value === undefined || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Read the worn equipment the host page embedded as a JSON island.
 * Profile + equipment views render <script id="cs-character-equipment">
 * containing either:
 *   - a {slot:itemId} map (PHP-style integer keys), or
 *   - a {slot:{id,slot}} map (already-shaped EquippedAppearanceItem),
 * The renderer only needs `id` per slot — we shape both forms here.
 */
function readEmbeddedEquipment(): PreviewEquipment {
  const tag = document.getElementById('cs-character-equipment');
  if (!tag || !tag.textContent) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(tag.textContent);
  } catch (err) {
    console.warn('[character3d] could not parse embedded equipment, ignoring', err);
    return {};
  }
  if (!raw || typeof raw !== 'object') return {};
  const out: PreviewEquipment = {};
  for (const [slot, value] of Object.entries(raw as Record<string, unknown>)) {
    const slotNum = Number(slot);
    if (!Number.isFinite(slotNum)) continue;
    if (value == null) continue;
    let id = NaN;
    if (typeof value === 'number') {
      id = value;
    } else if (typeof value === 'object' && 'id' in (value as Record<string, unknown>)) {
      id = Number((value as { id: unknown }).id);
    }
    if (!Number.isFinite(id) || id <= 0) continue;
    const item: EquippedAppearanceItem = { id, slot: slotNum };
    out[slotNum] = item;
  }
  return out;
}

function bootLazyMultiPreview(): boolean {
  const containers = document.querySelectorAll<HTMLDivElement>('.cs-multi-preview');
  if (containers.length === 0) return false;

  const controllers = new Map<HTMLDivElement, CharacterCreationPreviewController>();
  const mountPromises = new Map<HTMLDivElement, Promise<void>>();
  const renderOnceQueue: HTMLDivElement[] = [];
  let renderOnceRunning = false;
  let renderOnceCompleted = 0;
  let renderOnceFailed = 0;

  const updateRenderOnceStatus = (): void => {
    const w = window as unknown as {
      __csTestRenderOnce?: {
        total: number;
        completed: number;
        failed: number;
        running: boolean;
      };
    };
    w.__csTestRenderOnce = {
      total: Array.from(containers).filter((el) => el.dataset.renderOnce === '1').length,
      completed: renderOnceCompleted,
      failed: renderOnceFailed,
      running: renderOnceRunning,
    };
  };

  const readContainerConfig = (el: HTMLDivElement): {
    appearance: BaseCharacterAppearance;
    equipment: PreviewEquipment;
    animationStrategy: CharacterAnimationStrategy;
    presentationOverrides?: Record<string, EquipmentPresentationOverride>;
    autoRotate?: boolean;
    fixedRootYaw?: number;
    cameraDistance?: number;
    cameraPanY?: number;
    bob?: boolean;
    itemOnly?: boolean;
  } => {
    let appearance: BaseCharacterAppearance;
    try {
      const raw = el.dataset.appearance;
      appearance = normalizeBaseCharacterAppearance(
        raw ? (JSON.parse(raw) as BaseCharacterAppearance) : createDefaultBaseCharacterAppearance(),
      );
    } catch {
      appearance = normalizeBaseCharacterAppearance(createDefaultBaseCharacterAppearance());
    }

    const equipment: PreviewEquipment = {};
    const eqRaw = el.dataset.equipment;
    if (eqRaw) {
      try {
        const map = JSON.parse(eqRaw) as Record<string, number | { id: number; slot: number }>;
        for (const [slot, val] of Object.entries(map)) {
          const slotN = Number(slot);
          if (!Number.isFinite(slotN)) continue;
          const id = typeof val === 'number' ? val : Number(val?.id);
          if (Number.isFinite(id) && id > 0) {
            equipment[slotN] = { id, slot: slotN } as EquippedAppearanceItem;
          }
        }
      } catch {
        // ignore; render with no equipment
      }
    }

    return {
      appearance,
      equipment,
      animationStrategy: readAnimationStrategy(el.dataset.animationStrategy) ?? 'static-pose',
      presentationOverrides: readPresentationOverrides(el.dataset.presentationOverrides),
      autoRotate: readBooleanData(el.dataset.autoRotate),
      fixedRootYaw: readNumberData(el.dataset.fixedRootYaw),
      cameraDistance: readNumberData(el.dataset.cameraDistance),
      cameraPanY: readNumberData(el.dataset.cameraPanY),
      bob: readBooleanData(el.dataset.bob),
      itemOnly: readBooleanData(el.dataset.itemOnly),
    };
  };

  const exposeControllers = (): void => {
    const w = window as unknown as {
      __csTestControllers?: unknown[];
      __csTestControllerById?: Record<string, CharacterCreationPreviewController>;
    };
    w.__csTestControllers = [...controllers.values()];
    const byId: Record<string, CharacterCreationPreviewController> = {};
    for (const [el, controller] of controllers.entries()) {
      if (el.id) {
        byId[el.id] = controller;
      }
    }
    w.__csTestControllerById = byId;
  };

  const mountContainer = (el: HTMLDivElement): Promise<void> => {
    const existingMount = mountPromises.get(el);
    if (existingMount) {
      return existingMount;
    }
    if (controllers.has(el)) {
      return Promise.resolve();
    }
    const {
      appearance,
      equipment,
      animationStrategy,
      presentationOverrides,
      autoRotate,
      fixedRootYaw,
      cameraDistance,
      cameraPanY,
      bob,
      itemOnly,
    } = readContainerConfig(el);
    const controller = new CharacterCreationPreviewController(el, {
      animationStrategy,
      presentationOverrides,
      autoRotate,
      fixedRootYaw,
      cameraDistance,
      cameraPanY,
      bob,
      itemOnly,
    });
    controllers.set(el, controller);
    exposeControllers();
    const mountPromise = controller.mount(appearance, equipment).catch((err) => {
      controllers.delete(el);
      mountPromises.delete(el);
      exposeControllers();
      console.error('[character3d/multi] mount failed', err);
      throw err;
    });
    mountPromises.set(el, mountPromise);
    return mountPromise;
  };

  const requestMountContainer = (el: HTMLDivElement): void => {
    void mountContainer(el).catch(() => {
      // mountContainer already logs and clears failed controllers.
    });
  };

  const disposeContainer = (el: HTMLDivElement): void => {
    const controller = controllers.get(el);
    if (!controller) {
      return;
    }
    controller.dispose();
    controllers.delete(el);
    mountPromises.delete(el);
    exposeControllers();
  };

  const waitForPaint = async (): Promise<void> => {
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
  };

  const snapshotContainer = async (el: HTMLDivElement): Promise<void> => {
    if (el.dataset.renderOnceDone === '1' || el.dataset.renderOnceBusy === '1') {
      return;
    }

    el.dataset.renderOnceBusy = '1';
    el.dataset.renderOnceStatus = 'rendering';

    try {
      await mountContainer(el);
      await waitForPaint();
      await waitForPaint();
      const canvas = el.querySelector('canvas');
      if (!(canvas instanceof HTMLCanvasElement)) {
        throw new Error('preview canvas was not mounted');
      }

      const dataUrl = canvas.toDataURL('image/png');
      const img = document.createElement('img');
      img.className = 'cs-multi-preview__snapshot';
      img.alt = el.getAttribute('aria-label') || el.dataset.previewLabel || '3D equipment preview';
      img.src = dataUrl;
      img.width = canvas.width;
      img.height = canvas.height;
      img.decoding = 'async';
      img.style.display = 'block';
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'contain';

      disposeContainer(el);
      el.replaceChildren(img);
      el.dataset.renderOnceDone = '1';
      el.dataset.renderOnceStatus = 'done';
      renderOnceCompleted += 1;
    } catch (err) {
      renderOnceFailed += 1;
      el.dataset.renderOnceStatus = 'failed';
      console.error('[character3d/multi] render-once snapshot failed', err);
      disposeContainer(el);
    } finally {
      delete el.dataset.renderOnceBusy;
      updateRenderOnceStatus();
    }
  };

  const processRenderOnceQueue = async (): Promise<void> => {
    if (renderOnceRunning) {
      return;
    }
    renderOnceRunning = true;
    updateRenderOnceStatus();
    while (renderOnceQueue.length > 0) {
      const el = renderOnceQueue.shift();
      if (!el || el.dataset.renderOnceDone === '1') {
        continue;
      }
      await snapshotContainer(el);
    }
    renderOnceRunning = false;
    updateRenderOnceStatus();
  };

  const enqueueRenderOnce = (el: HTMLDivElement): void => {
    if (el.dataset.renderOnceDone === '1' || renderOnceQueue.includes(el)) {
      return;
    }
    renderOnceQueue.push(el);
    void processRenderOnceQueue();
  };

  const eagerContainers = Array.from(containers).filter((el) => el.dataset.snapshotUrl || el.dataset.keepalive === '1');
  eagerContainers.forEach(requestMountContainer);
  const renderOnceContainers = Array.from(containers).filter((el) => el.dataset.renderOnce === '1' && !eagerContainers.includes(el));
  const lazyContainers = Array.from(containers).filter((el) => !eagerContainers.includes(el) && !renderOnceContainers.includes(el));
  updateRenderOnceStatus();
  renderOnceContainers.forEach(enqueueRenderOnce);
  if (lazyContainers.length === 0) {
    return true;
  }

  if (!('IntersectionObserver' in window)) {
    lazyContainers.forEach(requestMountContainer);
    return true;
  }

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const el = entry.target;
      if (!(el instanceof HTMLDivElement)) {
        continue;
      }
      if (entry.isIntersecting) {
        requestMountContainer(el);
      } else {
        disposeContainer(el);
      }
    }
  }, {
    root: null,
    rootMargin: '280px 0px',
    threshold: 0.01,
  });

  lazyContainers.forEach((el) => observer.observe(el));
  return true;
}

function bootMultiPreview(): boolean {
  return bootLazyMultiPreview();
  // Dev / test grid pages render many side-by-side previews. Each container
  // div has class .cs-multi-preview and inline data attributes:
  //   data-appearance='{"kitByAppearanceSlot":...,"bodyColors":...}'
  //   data-equipment='{"0":1153,"4":1115,"7":1067}'  (slot id → item id)
  // Each gets its own controller; they all share the cached asset/animation
  // promises so the 45MB models JSON only loads once for the whole grid.
  const containers = document.querySelectorAll<HTMLDivElement>('.cs-multi-preview');
  if (containers.length === 0) return false;

  containers.forEach((el) => {
    let app: BaseCharacterAppearance;
    try {
      const raw = el.dataset.appearance;
      app = normalizeBaseCharacterAppearance(
        raw ? (JSON.parse(raw) as BaseCharacterAppearance) : createDefaultBaseCharacterAppearance(),
      );
    } catch {
      app = normalizeBaseCharacterAppearance(createDefaultBaseCharacterAppearance());
    }
    const eqRaw = el.dataset.equipment;
    const eq: PreviewEquipment = {};
    if (eqRaw) {
      try {
        const map = JSON.parse(eqRaw) as Record<string, number | { id: number; slot: number }>;
        for (const [slot, val] of Object.entries(map)) {
          const slotN = Number(slot);
          if (!Number.isFinite(slotN)) continue;
          const id = typeof val === 'number' ? val : Number(val?.id);
          if (Number.isFinite(id) && id > 0) {
            eq[slotN] = { id, slot: slotN } as EquippedAppearanceItem;
          }
        }
      } catch {
        // ignore — render with no equipment
      }
    }
    const controller = new CharacterCreationPreviewController(el, {
      animationStrategy: readAnimationStrategy(el.dataset.animationStrategy) ?? 'static-pose',
      presentationOverrides: readPresentationOverrides(el.dataset.presentationOverrides),
      autoRotate: readBooleanData(el.dataset.autoRotate),
      fixedRootYaw: readNumberData(el.dataset.fixedRootYaw),
      cameraDistance: readNumberData(el.dataset.cameraDistance),
      cameraPanY: readNumberData(el.dataset.cameraPanY),
      bob: readBooleanData(el.dataset.bob),
      itemOnly: readBooleanData(el.dataset.itemOnly),
    });
    controller.mount(app, eq).catch((err) => {
      console.error('[character3d/multi] mount failed', err);
    });
    // Expose for the dev /3d-test grid measurement console snippets.
    const w = window as unknown as { __csTestControllers?: unknown[] };
    if (!Array.isArray(w.__csTestControllers)) w.__csTestControllers = [];
    (w.__csTestControllers as unknown[]).push(controller);
  });
  return true;
}

function boot(): void {
  bootMultiPreview();

  const container = document.getElementById('character-create-preview');
  if (!(container instanceof HTMLDivElement)) {
    return;
  }

  // On profile pages the server embeds the saved appearance; otherwise start fresh.
  const seedScript = document.getElementById('cs-character-appearance');
  let seed: BaseCharacterAppearance | null = null;
  if (seedScript && seedScript.textContent) {
    try {
      seed = JSON.parse(seedScript.textContent) as BaseCharacterAppearance;
    } catch (err) {
      console.warn('[character3d] could not parse embedded appearance, using default', err);
      seed = null;
    }
  }

  let appearance: BaseCharacterAppearance = normalizeBaseCharacterAppearance(
    seed ?? createDefaultBaseCharacterAppearance(),
  );

  const hiddenInput = document.getElementById('character-appearance-input');
  const appearanceInput = hiddenInput instanceof HTMLInputElement ? hiddenInput : null;
  function syncHiddenInput(): void {
    if (appearanceInput) {
      appearanceInput.value = JSON.stringify(appearance);
    }
  }
  syncHiddenInput();

  const equipment = readEmbeddedEquipment();

  const controller = new CharacterCreationPreviewController(container, {
    animationStrategy: readAnimationStrategy(container.dataset.animationStrategy) ?? 'static-pose',
    presentationOverrides: readPresentationOverrides(container.dataset.presentationOverrides),
    autoRotate: readBooleanData(container.dataset.autoRotate),
    fixedRootYaw: readNumberData(container.dataset.fixedRootYaw),
    cameraDistance: readNumberData(container.dataset.cameraDistance),
    cameraPanY: readNumberData(container.dataset.cameraPanY),
    bob: readBooleanData(container.dataset.bob),
    itemOnly: readBooleanData(container.dataset.itemOnly),
  });
  controller.mount(appearance, equipment).catch((err) => {
    console.error('[character3d] failed to mount preview', err);
  });

  // Expose for the orbit-autorotate / snapshot-uploader scripts that
  // attach via window.csCharacterCreator (round 11 hook).
  (window as unknown as { csCharacterCreator?: unknown }).csCharacterCreator = {
    controller,
    container,
  };

  const slotIndex: Record<string, number> = {};
  for (const slot of BASE_APPEARANCE_SLOT_ORDER) {
    const opts = getBaseAppearanceOptions(slot);
    slotIndex[slot] = opts.findIndex((id) => id === appearance.kitByAppearanceSlot[String(slot)]);
    if (slotIndex[slot] < 0) slotIndex[slot] = 0;
  }
  const colorIndex: Record<string, number> = {};
  for (const channel of BODY_COLOR_CHANNELS) {
    const swatches = getBodyColorSwatches(channel);
    colorIndex[channel] = swatches.findIndex((s) => s.value === appearance.bodyColors[channel]);
    if (colorIndex[channel] < 0) colorIndex[channel] = 0;
  }

  function update(): void {
    syncHiddenInput();
    controller.updateAppearance(appearance).catch((err) => {
      console.error('[character3d] update failed', err);
    });
  }

  function cycleSlot(slot: BaseAppearanceSlot, direction: 1 | -1): void {
    const opts = getBaseAppearanceOptions(slot);
    slotIndex[slot] = ((slotIndex[slot] ?? 0) + direction + opts.length) % opts.length;
    const next = cloneBaseCharacterAppearance(appearance);
    next.kitByAppearanceSlot[String(slot)] = opts[slotIndex[slot]] ?? opts[0]!;
    appearance = normalizeBaseCharacterAppearance(next);
    update();
  }

  function cycleColor(channel: BodyColorChannel, direction: 1 | -1): void {
    const swatches = getBodyColorSwatches(channel);
    colorIndex[channel] = ((colorIndex[channel] ?? 0) + direction + swatches.length) % swatches.length;
    const next = cloneBaseCharacterAppearance(appearance);
    next.bodyColors[channel] = swatches[colorIndex[channel]]!.value;
    appearance = normalizeBaseCharacterAppearance(next);
    update();
    const swatch = document.querySelector<HTMLElement>(`[data-color-swatch="${channel}"]`);
    if (swatch) swatch.style.background = swatches[colorIndex[channel]]!.color;
  }

  function randomize(): void {
    const next = cloneBaseCharacterAppearance(appearance);
    for (const slot of BASE_APPEARANCE_SLOT_ORDER) {
      const opts = getBaseAppearanceOptions(slot);
      slotIndex[slot] = Math.floor(Math.random() * opts.length);
      next.kitByAppearanceSlot[String(slot)] = opts[slotIndex[slot]]!;
    }
    for (const channel of BODY_COLOR_CHANNELS) {
      const swatches = getBodyColorSwatches(channel);
      colorIndex[channel] = Math.floor(Math.random() * swatches.length);
      next.bodyColors[channel] = swatches[colorIndex[channel]]!.value;
      const swatch = document.querySelector<HTMLElement>(`[data-color-swatch="${channel}"]`);
      if (swatch) swatch.style.background = swatches[colorIndex[channel]]!.color;
    }
    appearance = normalizeBaseCharacterAppearance(next);
    update();
  }

  document.querySelectorAll<HTMLButtonElement>('[data-cycle-slot]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const slot = Number(btn.dataset.cycleSlot) as BaseAppearanceSlot;
      const dir = (btn.dataset.cycleDir === 'prev' ? -1 : 1) as 1 | -1;
      cycleSlot(slot, dir);
    });
  });

  document.querySelectorAll<HTMLButtonElement>('[data-cycle-color]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const channel = btn.dataset.cycleColor as BodyColorChannel;
      const dir = (btn.dataset.cycleDir === 'prev' ? -1 : 1) as 1 | -1;
      cycleColor(channel, dir);
    });
  });

  const randomizeBtn = document.getElementById('cs-char-randomize');
  if (randomizeBtn) randomizeBtn.addEventListener('click', randomize);

  for (const channel of BODY_COLOR_CHANNELS) {
    const swatch = document.querySelector<HTMLElement>(`[data-color-swatch="${channel}"]`);
    if (swatch) {
      const swatches = getBodyColorSwatches(channel);
      swatch.style.background = swatches[colorIndex[channel] ?? 0]!.color;
    }
  }

  const altGoalRow = document.getElementById('cs-alt-goal-row');
  const buildRadios = document.querySelectorAll<HTMLInputElement>('input[name="build_type"]');
  function applyBuildVisibility(): void {
    if (!altGoalRow) return;
    let selected = 'main';
    buildRadios.forEach((r) => {
      if (r.checked) selected = r.value;
    });
    altGoalRow.style.display = selected === 'alt' ? '' : 'none';
  }
  if (buildRadios.length > 0) {
    buildRadios.forEach((r) => r.addEventListener('change', applyBuildVisibility));
    applyBuildVisibility();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
